import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Calendar, MoreVertical, Pencil, Trash2 } from 'lucide-react';
import { apiGet, apiPost, apiDelete, apiPatch } from '../lib/api';
import { useToast } from '../contexts/ToastContext';

interface MeetingSeries {
  id: string;
  name: string;
  description: string | null;
  cadence: string | null;
  instance_count: number;
  created_at: string;
}

interface StandaloneMeeting {
  id: string;
  title: string | null;
  meeting_date: string;
  summary: string | null;
  created_at: string;
}

export function MeetingsPage() {
  const toast = useToast();
  const [series, setSeries] = useState<MeetingSeries[]>([]);
  const [standaloneMeetings, setStandaloneMeetings] = useState<StandaloneMeeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showStandaloneModal, setShowStandaloneModal] = useState(false);
  const [editingSeries, setEditingSeries] = useState<MeetingSeries | null>(null);
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);

  async function loadData() {
    try {
      const [seriesData, standaloneData] = await Promise.all([
        apiGet<MeetingSeries[]>('/meeting-series'),
        apiGet<StandaloneMeeting[]>('/meeting-instances/standalone'),
      ]);
      setSeries(seriesData);
      setStandaloneMeetings(standaloneData);
    } catch (err) {
      console.error('Failed to load meetings:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadData(); }, []);

  async function handleCreate(name: string, description: string, cadence: string) {
    try {
      await apiPost('/meeting-series', { name, description: description || null, cadence: cadence || null });
      setShowCreateModal(false);
      toast.success('Meeting series created');
      loadData();
    } catch (err) {
      console.error('Failed to create meeting series:', err);
      toast.error('Failed to create meeting series');
    }
  }

  async function handleUpdate(id: string, name: string, description: string, cadence: string) {
    try {
      await apiPatch(`/meeting-series/${id}`, { name, description: description || null, cadence: cadence || null });
      setEditingSeries(null);
      toast.success('Meeting series updated');
      loadData();
    } catch (err) {
      console.error('Failed to update meeting series:', err);
      toast.error('Failed to update meeting series');
    }
  }

  async function handleCreateStandalone(title: string, meetingDate: string) {
    try {
      await apiPost('/meeting-instances', { title, meeting_date: meetingDate });
      setShowStandaloneModal(false);
      toast.success('Meeting created');
      loadData();
    } catch (err) {
      console.error('Failed to create meeting:', err);
      toast.error('Failed to create meeting');
    }
  }

  async function handleDeleteStandalone(id: string) {
    if (!window.confirm('Are you sure you want to delete this meeting?')) return;
    try {
      await apiDelete(`/meeting-instances/${id}`);
      toast.success('Meeting deleted');
      loadData();
    } catch (err) {
      console.error('Failed to delete meeting:', err);
      toast.error('Failed to delete meeting');
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm('Are you sure you want to delete this meeting series and all its instances?')) return;
    try {
      await apiDelete(`/meeting-series/${id}`);
      toast.success('Meeting series deleted');
      loadData();
    } catch (err) {
      console.error('Failed to delete meeting series:', err);
      toast.error('Failed to delete meeting series');
    }
  }

  if (loading) {
    return (
      <div className="h-full overflow-auto p-3 sm:p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-3 sm:p-6">
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-text-primary">Meetings</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowStandaloneModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-bg-card border border-border text-text-secondary text-sm font-medium hover:bg-bg-hover transition-colors"
          >
            <Plus size={16} />
            One-Off
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-accent text-white text-sm font-medium hover:bg-accent-hover transition-colors"
          >
            <Plus size={16} />
            New Series
          </button>
        </div>
      </div>

      {/* Meeting Series */}
      {series.length > 0 && (
        <div className="mb-8">
          <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wider mb-3">Meeting Series</h2>
          <div className="space-y-3">
            {series.map((s) => (
              <div
                key={s.id}
                className="bg-bg-card border border-border rounded-lg p-4 hover:border-border-hover transition-colors group"
              >
                <div className="flex items-start justify-between">
                  <Link to={`/meetings/${s.id}`} className="flex-1 min-w-0">
                    <h3 className="text-base font-semibold text-text-primary group-hover:text-accent transition-colors">
                      {s.name}
                    </h3>
                    {s.description && (
                      <p className="text-sm text-text-muted mt-1 truncate">{s.description}</p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-xs text-text-muted">
                      {s.cadence && <span className="capitalize">{s.cadence}</span>}
                      <span>{s.instance_count} meeting{s.instance_count !== 1 ? 's' : ''}</span>
                    </div>
                  </Link>
                  <div className="relative">
                    <button
                      onClick={(e) => { e.preventDefault(); setMenuOpenId(menuOpenId === s.id ? null : s.id); }}
                      className="p-1.5 rounded-md text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors"
                    >
                      <MoreVertical size={16} />
                    </button>
                    {menuOpenId === s.id && (
                      <div className="absolute right-0 top-8 z-10 w-36 bg-bg-surface border border-border rounded-md shadow-lg py-1">
                        <button
                          onClick={() => { setEditingSeries(s); setMenuOpenId(null); }}
                          className="flex items-center gap-2 w-full px-3 py-1.5 text-sm text-text-secondary hover:bg-bg-hover transition-colors"
                        >
                          <Pencil size={14} /> Edit
                        </button>
                        <button
                          onClick={() => { handleDelete(s.id); setMenuOpenId(null); }}
                          className="flex items-center gap-2 w-full px-3 py-1.5 text-sm text-danger hover:bg-bg-hover transition-colors"
                        >
                          <Trash2 size={14} /> Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* One-Off Meetings */}
      {standaloneMeetings.length > 0 && (
        <div className="mb-8">
          <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wider mb-3">One-Off Meetings</h2>
          <div className="space-y-1">
            {standaloneMeetings.map((m) => (
              <div
                key={m.id}
                className="flex items-center gap-3 px-4 py-3 bg-bg-card border border-border rounded-lg hover:border-border-hover transition-colors group"
              >
                <Calendar size={16} className="text-warning flex-shrink-0" />
                <Link to={`/meetings/standalone/${m.id}`} className="flex-1 min-w-0">
                  <span className="text-sm text-text-primary group-hover:text-accent transition-colors">
                    {m.title || 'Untitled Meeting'}
                  </span>
                  <span className="text-xs text-text-muted ml-2">
                    {formatShortDate(m.meeting_date)}
                  </span>
                </Link>
                <button
                  onClick={() => handleDeleteStandalone(m.id)}
                  className="opacity-0 group-hover:opacity-100 p-1 text-text-muted hover:text-danger transition-all"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {series.length === 0 && standaloneMeetings.length === 0 && (
        <div className="text-center py-16 text-text-muted">
          <Calendar size={48} className="mx-auto mb-4 opacity-50" />
          <p className="text-lg mb-2">No meetings yet</p>
          <p className="text-sm">Create a meeting series or a one-off meeting to get started.</p>
        </div>
      )}

      {(showCreateModal || editingSeries) && (
        <SeriesModal
          series={editingSeries}
          onSave={(name, desc, cadence) =>
            editingSeries
              ? handleUpdate(editingSeries.id, name, desc, cadence)
              : handleCreate(name, desc, cadence)
          }
          onClose={() => { setShowCreateModal(false); setEditingSeries(null); }}
        />
      )}

      {showStandaloneModal && (
        <StandaloneModal
          onSave={handleCreateStandalone}
          onClose={() => setShowStandaloneModal(false)}
        />
      )}
    </div>
    </div>
  );
}

function SeriesModal({
  series,
  onSave,
  onClose,
}: {
  series: MeetingSeries | null;
  onSave: (name: string, description: string, cadence: string) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(series?.name ?? '');
  const [description, setDescription] = useState(series?.description ?? '');
  const [cadence, setCadence] = useState(series?.cadence ?? '');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="bg-bg-surface border border-border rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-text-primary mb-4">
          {series ? 'Edit Meeting Series' : 'New Meeting Series'}
        </h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-text-secondary mb-1">Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Weekly Ops Sync"
              autoFocus
              className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent text-sm"
            />
          </div>
          <div>
            <label className="block text-sm text-text-secondary mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description..."
              rows={2}
              className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent text-sm resize-none"
            />
          </div>
          <div>
            <label className="block text-sm text-text-secondary mb-1">Cadence</label>
            <select
              value={cadence}
              onChange={(e) => setCadence(e.target.value)}
              className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary focus:outline-none focus:border-accent text-sm"
            >
              <option value="">None</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="biweekly">Biweekly</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="adhoc">Ad hoc</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => name.trim() && onSave(name.trim(), description, cadence)}
            disabled={!name.trim()}
            className="px-4 py-2 text-sm bg-accent text-white rounded-md hover:bg-accent-hover transition-colors disabled:opacity-50"
          >
            {series ? 'Save Changes' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

function StandaloneModal({
  onSave,
  onClose,
}: {
  onSave: (title: string, meetingDate: string) => void;
  onClose: () => void;
}) {
  const [title, setTitle] = useState('');
  const [meetingDate, setMeetingDate] = useState(new Date().toISOString().split('T')[0]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="bg-bg-surface border border-border rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-text-primary mb-4">New One-Off Meeting</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-text-secondary mb-1">Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Coffee Chat with John"
              autoFocus
              className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent text-sm"
            />
          </div>
          <div>
            <label className="block text-sm text-text-secondary mb-1">Date *</label>
            <input
              type="date"
              value={meetingDate}
              onChange={(e) => setMeetingDate(e.target.value)}
              className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary focus:outline-none focus:border-accent text-sm"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => title.trim() && meetingDate && onSave(title.trim(), meetingDate)}
            disabled={!title.trim() || !meetingDate}
            className="px-4 py-2 text-sm bg-accent text-white rounded-md hover:bg-accent-hover transition-colors disabled:opacity-50"
          >
            Create
          </button>
        </div>
      </div>
    </div>
  );
}

function formatShortDate(d: string): string {
  return new Date(d + (d.includes('T') ? '' : 'T00:00:00')).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
